import { ImagePreviewModal } from 'Components/Chat/ChatLayout/FilesPreview/ImagePreviewModal';
import { ChatLayoutContext } from 'Components/Chat/contexts/ChatLayoutContext';
import LazyLoader from 'Components/LazyLoader';
import { MobXProviderContext, observer } from 'mobx-react';
import { SharedItemRow } from 'Modules/sharedItems/components/SharedItemRow';
import { SharedItemsEmptyState } from 'Modules/sharedItems/components/SharedItemsEmptyState';
import { SharedItemsListHeader } from 'Modules/sharedItems/components/SharedItemsListHeader';
import { SharedItemsPageHeader } from 'Modules/sharedItems/components/SharedItemsPageHeader';
import { useConversationDisplayName } from 'Modules/sharedItems/hooks/useConversationDisplayName/index';
import { useOpenSharedItem } from 'Modules/sharedItems/hooks/useOpenSharedItem/index';
import { ANALYTICS_EVENT_SHARED_FILES_LINKS } from 'Modules/sharedItems/index.constants';
import type { SharedItem } from 'Modules/sharedItems/index.types';
import { useSharedItems } from 'Modules/sharedItems/requests/index.requests';
import { useUIStore } from 'Modules/ui/index.store';
import React from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useParams } from 'react-router';
import type { RootStoreProps } from 'Stores/RootStore.types';
import { pushToGTMDataLayer } from 'Utils/analytics';

const SCROLLABLE_TARGET_ID = 'shared-items-list';

export const SharedFilesAndLinksView: React.FC = observer(() => {
  const { setView } = React.useContext(ChatLayoutContext);
  const {
    conversation: { CurrentConversation },
  } = React.useContext<RootStoreProps>(MobXProviderContext);
  const { conversationId } = useParams();
  const conversationName = useConversationDisplayName(CurrentConversation);
  const isImagePreviewOpen = useUIStore((state) => state.isImagePreviewOpen);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isError,
    isFetchingNextPage,
  } = useSharedItems(conversationId);

  const {
    handleOpenSharedItem,
    handleCloseImagePreview,
    handleDownloadPreviewImage,
    previewItem,
    previewDocument,
    previewSenderName,
    imageUrl,
    isLoadingImage,
  } = useOpenSharedItem();

  const sharedItems = React.useMemo(
    () => data?.pages.flatMap((page) => page.sharedItems) ?? [],
    [data?.pages]
  );

  React.useEffect(() => {
    if (!conversationId) {
      return;
    }

    pushToGTMDataLayer(ANALYTICS_EVENT_SHARED_FILES_LINKS.VIEW_OPENED, {
      conversation_id: conversationId,
    });
  }, [conversationId]);

  const handleBackToChat = () => {
    setView('');
  };

  const handleLoadMore = () => {
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }

    void fetchNextPage();
  };

  const handleOpenItem = (item: SharedItem) => {
    void handleOpenSharedItem(item);
  };

  return (
    <div
      className="semantic-flex-column semantic-flex-fluid flex min-h-0 flex-1 flex-col mt-4"
      data-testid="shared-files-and-links-view"
    >
      <SharedItemsPageHeader
        conversationName={conversationName}
        onBack={handleBackToChat}
      />

      <div className="mt-4 flex min-h-0 flex-1 flex-col">
        {isLoading ? (
          <div className="flex min-h-0 flex-1 items-center justify-center">
            <LazyLoader />
          </div>
        ) : isError && !data ? (
          <SharedItemsEmptyState
            title="Couldn't load files"
            description={`Something went wrong while fetching the shared files in this\nchat. Don't worry, they are still safe.`}
            descriptionClassName="whitespace-pre-line min-w-[400px]"
          />
        ) : sharedItems.length === 0 ? (
          <SharedItemsEmptyState
            title="No files shared yet"
            description={`Uploaded documents or shared file pages in your chat, will\n appear in this workspace.`}
          />
        ) : (
          <div
            role="table"
            aria-label="Shared files and links"
            className="flex min-h-0 flex-1 flex-col"
          >
            <SharedItemsListHeader />
            <div
              id={SCROLLABLE_TARGET_ID}
              // Presentational so the rowgroup below stays owned by the table.
              role="presentation"
              className="min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable]"
            >
              <div role="rowgroup">
                <InfiniteScroll
                  dataLength={sharedItems.length}
                  next={handleLoadMore}
                  hasMore={!!hasNextPage}
                  loader={<LazyLoader className="py-4" />}
                  endMessage={<></>}
                  scrollableTarget={SCROLLABLE_TARGET_ID}
                  className="!px-0"
                >
                  {sharedItems.map((item) => (
                    <SharedItemRow
                      key={item.id}
                      item={item}
                      onOpenItem={handleOpenItem}
                    />
                  ))}
                </InfiniteScroll>
              </div>
            </div>
          </div>
        )}
      </div>

      {previewDocument && (
        <ImagePreviewModal
          isOpen={
            !!previewItem &&
            isImagePreviewOpen === (previewItem.fileInfoId || previewItem.id)
          }
          onClose={handleCloseImagePreview}
          image={previewDocument}
          imageUrl={imageUrl}
          isLoadingImage={isLoadingImage}
          senderName={previewSenderName}
          messageDate={previewItem?.sharedAt || ''}
          onDownload={handleDownloadPreviewImage}
        />
      )}
    </div>
  );
});
